/**
 * FHIR Write-Back service.
 *
 * v1: Creates a DocumentReference for each accepted code.
 * v2 (future): Creates a Condition resource with verificationStatus=provisional.
 *
 * Both methods use idempotency — the Raapid suggestion ID is embedded as a
 * DocumentReference.identifier so duplicate submissions are detected server-side.
 */
import type { CodingSuggestion, Patient, Encounter } from '../types';

type FHIRClient = {
  request: (opts: { url: string; method: string; body?: string; headers?: Record<string, string> }) => Promise<unknown>;
  state: { serverUrl: string; tokenResponse?: { access_token?: string } };
};

// ─── v1: DocumentReference ────────────────────────────────────────────────────

export async function writeDocumentReference(
  client: FHIRClient,
  suggestion: CodingSuggestion,
  patient: Patient,
  encounter: Encounter,
): Promise<{ fhirResourceId: string; fhirResponseStatus: number }> {
  const now = new Date().toISOString();

  const resource = {
    resourceType: 'DocumentReference',
    status: 'current',
    type: {
      coding: [
        {
          system: 'http://loinc.org',
          code: '11506-3',
          display: 'Progress note',
        },
      ],
    },
    category: [
      {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/v2-0131',
            code: 'CP',
            display: 'Contact Party',
          },
        ],
      },
    ],
    subject: {
      reference: `Patient/${patient.mrn}`,
      display: `${patient.firstName} ${patient.lastName}`,
    },
    date: now,
    author: [{ display: encounter.attendingPhysician }],
    description: `RAAPID Risk Adjustment — Accepted Code: ${suggestion.icdCode}`,
    identifier: [
      {
        system: 'https://raapid.ai/suggestion-id',
        value: suggestion.id,
      },
    ],
    content: [
      {
        attachment: {
          contentType: 'text/plain',
          language: 'en-US',
          title: `Risk Adjustment Suggestion — ${suggestion.icdCode}`,
          creation: now,
          data: btoa(buildDocumentContent(suggestion, encounter)),
        },
      },
    ],
    context: {
      encounter: [{ reference: `Encounter/${encounter.encounterId}` }],
      period: {
        start: encounter.encounterDate,
        end: encounter.encounterDate,
      },
    },
    // RAAPID extension: embed HCC metadata
    extension: [
      {
        url: 'https://raapid.ai/fhir/StructureDefinition/hcc-weight',
        valueDecimal: suggestion.hccWeight,
      },
      {
        url: 'https://raapid.ai/fhir/StructureDefinition/gap-type',
        valueCode: suggestion.gapType,
      },
    ],
  };

  const response = await client.request({
    url: 'DocumentReference',
    method: 'POST',
    body: JSON.stringify(resource),
    headers: { 'Content-Type': 'application/fhir+json' },
  }) as { id?: string };

  return {
    fhirResourceId: `DocRef-${response.id ?? suggestion.id}`,
    fhirResponseStatus: 201,
  };
}

// ─── v2: Condition (future, requires Condition.write scope) ──────────────────

export async function writeCondition(
  client: FHIRClient,
  suggestion: CodingSuggestion,
  patient: Patient,
  encounter: Encounter,
): Promise<{ fhirResourceId: string; fhirResponseStatus: number }> {
  const resource = {
    resourceType: 'Condition',
    clinicalStatus: {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
          code: 'active',
        },
      ],
    },
    verificationStatus: {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
          // provisional = clinician accepted but not yet confirmed by billing
          code: 'provisional',
        },
      ],
    },
    code: {
      coding: [
        {
          system: 'http://hl7.org/fhir/sid/icd-10-cm',
          code: suggestion.icdCode,
          display: suggestion.icdDescription,
        },
      ],
      text: suggestion.icdDescription,
    },
    subject: { reference: `Patient/${patient.mrn}` },
    encounter: { reference: `Encounter/${encounter.encounterId}` },
    recordedDate: new Date().toISOString(),
    identifier: [
      {
        system: 'https://raapid.ai/suggestion-id',
        value: suggestion.id,
      },
    ],
    note: [
      {
        text: `Risk adjustment recapture — ${suggestion.gapType}. ${suggestion.clinicalReasoning}`,
      },
    ],
  };

  const response = await client.request({
    url: 'Condition',
    method: 'POST',
    body: JSON.stringify(resource),
    headers: { 'Content-Type': 'application/fhir+json' },
  }) as { id?: string };

  return {
    fhirResourceId: `Condition-${response.id ?? suggestion.id}`,
    fhirResponseStatus: 201,
  };
}

// ─── Mock write (mock / extension mode) ─────────────────────────────────────

export async function mockWrite(
  suggestion: CodingSuggestion,
): Promise<{ fhirResourceId: string; fhirResponseStatus: number }> {
  // Simulate network latency
  await new Promise((r) => setTimeout(r, 800));

  // Simulate occasional failure for demo (5% chance)
  if (Math.random() < 0.05) {
    throw new Error('FHIR server returned 503 — retry');
  }

  return {
    fhirResourceId: `DocRef-MOCK-${suggestion.id}-${Date.now()}`,
    fhirResponseStatus: 201,
  };
}

// ─── Graceful shutdown beacon ─────────────────────────────────────────────────
// Called when the EHR chart closes (beforeunload / pagehide events)

export function sendShutdownBeacon(sessionId: string, acceptedIds: string[]): void {
  const payload = JSON.stringify({ sessionId, acceptedIds, closedAt: new Date().toISOString() });
  navigator.sendBeacon('/api/v1/session/close', payload);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildDocumentContent(suggestion: CodingSuggestion, encounter: Encounter): string {
  return [
    `RAAPID Risk Adjustment Suggestion — Accepted`,
    `---`,
    `ICD-10: ${suggestion.icdCode} — ${suggestion.icdDescription}`,
    `HCC: ${suggestion.hccCategory ?? 'Non-HCC'}`,
    `Gap Type: ${suggestion.gapType}`,
    ``,
    `Clinical Reasoning:`,
    suggestion.clinicalReasoning,
    ``,
    `Evidence Sources:`,
    ...suggestion.evidence.map((e) => `  • [${e.documentDate}] ${e.sourceDocument}`),
    ``,
    `Encounter: ${encounter.encounterId} — ${encounter.encounterDate}`,
    `Attending: ${encounter.attendingPhysician}`,
    ``,
    `Generated by RAAPID v1.0`,
  ].join('\n');
}
